import type { FlexProps } from '@chakra-ui/react';
import { Flex } from '@chakra-ui/react';
import type { FractionIsh } from 'bi-fraction';
import { Fraction, RoundingMode } from 'bi-fraction';
import React, { useMemo } from 'react';

import { Percent } from '../Num';

type InputChipsProps = Omit<FlexProps, 'onChange'> & {
  value: FractionIsh;
  maxValue: FractionIsh | undefined | null;
  onChange: (value: string) => void;
  decimalPlaces: number;
};

export const InputChips = ({
  value,
  maxValue,
  onChange,
  decimalPlaces,
  ...props
}: InputChipsProps) => {
  const maxValueFraction = useMemo(() => {
    return maxValue ? new Fraction(maxValue) : Fraction.ZERO;
  }, [maxValue]);

  const percentageValueList = useMemo((): {
    value: string;
    percent: Fraction;
  }[] => {
    if (!maxValue) {
      return [];
    }
    return [...Array(4)].map((_, i) => {
      const percent = new Fraction(i + 1, 4);
      const valueFraction = percent.eq(1)
        ? maxValueFraction
        : maxValueFraction.mul(percent);

      return {
        percent,
        value: valueFraction.toFixed(decimalPlaces, {
          decimalPlaces,
          roundingMode: RoundingMode.ROUND_DOWN,
          trailingZeros: false,
        }),
      };
    });
  }, [decimalPlaces, maxValue, maxValueFraction]);

  if (!maxValue || maxValueFraction.lte(0)) {
    return null;
  }

  return (
    <Flex gap="8px" {...props}>
      {percentageValueList.map((item, i) => {
        const isActive = item.value === value;

        return (
          <InputChip
            key={i}
            isActive={isActive}
            onClick={() => {
              onChange(item.value);
            }}
          >
            {item.percent.eq(1) ? 'MAX' : <Percent value={item.percent} />}
          </InputChip>
        );
      })}
    </Flex>
  );
};

type InputChipProps = FlexProps & {
  isActive: boolean;
};
export const InputChip = ({ isActive, ...props }: InputChipProps) => {
  return (
    <Flex
      flex="1"
      justify="center"
      rounded="4px"
      px="9px"
      py="4px"
      color={isActive ? 'brand.black' : 'brand.white'}
      bg={
        isActive ? 'gradients.blue-green-horizontal' : 'gradients.sky-blue-20'
      }
      cursor="pointer"
      textStyle="caption"
      _hover={
        isActive
          ? undefined
          : {
              bg: 'gradients.blue-green-horizontal',
              filter: 'brightness(1.3)',
              color: 'brand.black',
            }
      }
      {...props}
    />
  );
};
